import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionLabel";
import { COMMITTEES } from "@/data/eventData";

const CommitteesPreview = () => (
    <section id="committees" data-testid="committees-preview-section" className="scroll-mt-24 py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <Reveal>
                <SectionLabel index="04" text="The Committees" />
            </Reveal>
            <div className="mt-8 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                <Reveal delay={0.1}>
                    <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
                        Eight Rooms.
                        <br />
                        One <em className="italic text-scarlet">Floor.</em>
                    </h2>
                </Reveal>
                <Reveal delay={0.2}>
                    <Link
                        to="/committees"
                        data-testid="view-all-committees-btn"
                        className="group hidden w-fit items-center gap-3 border border-crimson/60 px-7 py-4 font-sans text-[11px] font-semibold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-maroon/60 md:inline-flex"
                    >
                        View All Committees
                        <ArrowRight className="h-4 w-4 text-scarlet transition-transform duration-300 group-hover:translate-x-1.5" />
                    </Link>
                </Reveal>
            </div>
        </div>

        {/* IPL Auction — spotlight card */}
        <div className="mx-auto mt-14 max-w-[1400px] px-5 md:px-10">
            <Reveal>
                <Link
                    to="/committees#c-ipl-auction"
                    data-testid="auction-spotlight-card"
                    className="group relative flex flex-col gap-6 overflow-hidden border border-crimson/60 bg-maroon/30 p-7 transition-colors duration-500 hover:bg-maroon/50 md:flex-row md:items-center md:gap-10 md:p-10"
                >
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 bg-[radial-gradient(ellipse_60%_120%_at_90%_50%,rgba(201,31,38,0.2),transparent_65%)]"
                    />
                    <span className="absolute right-5 top-5 bg-crimson px-3 py-1 font-sans text-[9px] font-bold uppercase tracking-[0.3em] text-cream">
                        New
                    </span>
                    <span className="relative flex h-20 w-24 shrink-0 items-center justify-center border border-crimson/50 font-serif text-3xl font-light italic text-scarlet md:h-24 md:w-28">
                        IPL
                    </span>
                    <div className="relative flex-1">
                        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.35em] text-scarlet">
                            Special Event
                        </p>
                        <h3 className="mt-3 font-serif text-3xl font-light tracking-tight text-cream md:text-5xl">
                            The IPL Auction
                        </h3>
                        <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-ash">
                            Bid. Strategise. Build the dream squad — franchises battle
                            under a fixed purse, live at Chapter 4.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-sans text-[10px] font-semibold uppercase tracking-[0.25em] text-ash">
                            <span>₹3,000 per team</span>
                            <span className="text-scarlet">₹20,000 in prizes</span>
                            <span>Auctioneers · Mrinal Verma &amp; Abhishek Rai</span>
                        </div>
                    </div>
                    <span className="relative inline-flex items-center gap-3 font-sans text-[11px] font-semibold uppercase tracking-[0.3em] text-cream transition-colors duration-300 group-hover:text-scarlet">
                        View Details
                        <ArrowRight className="h-4 w-4 text-scarlet transition-transform duration-300 group-hover:translate-x-1.5" />
                    </span>
                </Link>
            </Reveal>
        </div>

        {/* mobile: horizontal carousel · desktop: bento grid */}
        <div className="no-scrollbar mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 md:grid md:grid-cols-2 md:gap-5 md:overflow-visible md:px-10 lg:grid-cols-3 mx-auto max-w-[1400px]">
            {COMMITTEES.filter((c) => c.id !== "ipl-auction").map((committee, i) => (
                <Reveal
                    key={committee.id}
                    delay={(i % 3) * 0.08}
                    className={`w-[78vw] shrink-0 snap-center sm:w-[320px] md:w-auto md:shrink ${
                        i === 0 ? "lg:col-span-2" : ""
                    }`}
                >
                    <Link
                        to={`/committees#c-${committee.id}`}
                        data-testid={`committee-card-${committee.id}`}
                        className="group flex h-full min-h-[220px] flex-col justify-between border border-cream/5 bg-coal p-7 transition-colors duration-500 hover:border-crimson/60 md:min-h-[240px] md:p-9"
                    >
                        <div>
                            <div className="flex items-start justify-between gap-4">
                                {committee.logo ? (
                                    <img
                                        src={committee.logo}
                                        alt={`${committee.name} emblem`}
                                        loading="lazy"
                                        className="h-20 w-auto object-contain transition-transform duration-500 group-hover:scale-105 md:h-24"
                                    />
                                ) : (
                                    <span className="flex h-20 items-center border border-crimson/40 px-6 font-serif text-2xl font-light italic text-scarlet md:h-24 md:text-3xl">
                                        {committee.monogram || committee.name}
                                    </span>
                                )}
                                <span className="font-sans text-[10px] font-semibold tracking-[0.3em] text-ash/50">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <h3 className="mt-5 font-serif text-3xl font-light tracking-tight text-cream transition-colors duration-300 group-hover:text-scarlet md:text-4xl">
                                {committee.name}
                            </h3>
                            <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-ash">
                                {committee.short}
                            </p>
                        </div>
                        <span
                            data-testid={`committee-view-details-${committee.id}`}
                            className="mt-8 inline-flex items-center gap-2.5 font-sans text-[10px] font-semibold uppercase tracking-[0.3em] text-cream/70 transition-colors duration-300 group-hover:text-scarlet"
                        >
                            View Details
                            <ArrowRight className="h-3.5 w-3.5 text-scarlet transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                    </Link>
                </Reveal>
            ))}

            {/* fills the last grid cell on desktop */}
            <Reveal delay={0.15} className="hidden md:block">
                <Link
                    to="/committees"
                    data-testid="view-all-committees-card"
                    className="group flex h-full min-h-[240px] flex-col items-start justify-between border border-crimson/50 bg-maroon/30 p-9 transition-colors duration-500 hover:bg-maroon/60"
                >
                    <p className="font-serif text-3xl font-light italic leading-snug text-cream md:text-4xl">
                        Find your
                        <br />
                        committee.
                    </p>
                    <span className="inline-flex items-center gap-3 font-sans text-[11px] font-semibold uppercase tracking-[0.3em] text-scarlet">
                        View All Committees
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                    </span>
                </Link>
            </Reveal>
        </div>

        <div className="mt-10 px-5 md:hidden">
            <Link
                to="/committees"
                data-testid="view-all-committees-btn-mobile"
                className="group flex w-full items-center justify-center gap-3 border border-crimson/60 px-7 py-4 font-sans text-[11px] font-semibold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-maroon/60"
            >
                View All Committees
                <ArrowRight className="h-4 w-4 text-scarlet transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
        </div>
    </section>
);

export default CommitteesPreview;
